import { Button } from 'antd';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { POPUP_MIN_DISPLAY_MS } from './types/reminder';
import { ClocksImg } from 'utils/assets/images';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 360px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease;
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Icon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;

const Title = styled.span`
  font-size: 18px;
  font-weight: 600;
`;

const Message = styled.p`
  margin: 0 0 20px;
  font-size: 16px;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Hint = styled.span`
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 12px;
`;

type ReminderPopupProps = {
  message: string;
  reminderId: string;
  studentId: string;
  onSnooze: (reminderId: string, studentId: string) => void;
  onDismiss: (reminderId: string, studentId: string) => void;
  snoozeCount: number;
  maxSnooze?: number;
};

export default function ReminderPopup({
  message,
  reminderId,
  studentId,
  onSnooze,
  onDismiss,
  snoozeCount,
  maxSnooze = 2,
}: ReminderPopupProps) {
  const snoozeMaxed = snoozeCount >= maxSnooze;
  const [canClose, setCanClose] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(
    Math.ceil(POPUP_MIN_DISPLAY_MS / 1000)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setCanClose(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSnooze = () => {
    onSnooze(reminderId, studentId);
  };

  const handleDismiss = () => {
    if (!canClose && !snoozeMaxed) return;
    onDismiss(reminderId, studentId);
  };

  return (
    <Overlay>
      <Card>
        <Header>
          <Icon src={ClocksImg} alt="" />
          <Title>Reminder</Title>
        </Header>
        <Message>{message}</Message>
        <Actions>
          <Button onClick={handleSnooze} disabled={snoozeMaxed}>
            Snooze
            {snoozeMaxed && ' (max)'}
          </Button>
          <Button type="primary" onClick={handleDismiss} disabled={!canClose && !snoozeMaxed}>
            Dismiss
          </Button>
        </Actions>
        {!canClose && !snoozeMaxed && (
          <Hint>You can dismiss in {secondsLeft} seconds</Hint>
        )}
      </Card>
    </Overlay>
  );
}
